import React from 'react';
import type { NodeProps } from 'reactflow';
import type { ChatDagNodeData, ChatDagNodeType } from '../types';

// Registry-boundary type narrower. Extracts the concrete node-data
// branch for a given `kind` and renders the component only when the
// discriminant matches at runtime. Eliminates the `any` casts that
// accumulated in v0.2's minimap color dispatch and makes adding new
// node types (cache_hit, create_artifacts fan) a one-line add.
export function byKind<K extends ChatDagNodeType>(
  kind: K,
  Comp: React.FC<NodeProps<Extract<ChatDagNodeData, { kind: K }>>>,
): React.FC<NodeProps<ChatDagNodeData>> {
  const Wrapped: React.FC<NodeProps<ChatDagNodeData>> = (props) => {
    if (props.data.kind !== kind) {
      // Unreachable at runtime because ReactFlow dispatches by
      // node.type which maps to our `kind` field. Fail loud in dev
      // so a registry bug doesn't render silently.
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          `[byKind] node dispatched to '${kind}' renderer but data.kind is '${props.data.kind}'`,
        );
      }
      return null;
    }
    return <Comp {...(props as NodeProps<Extract<ChatDagNodeData, { kind: K }>>)} />;
  };
  Wrapped.displayName = `byKind(${kind})`;
  return Wrapped;
}
